import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import {
  MOOTORI_ERP_SSO_ROUNDTRIP_KEY,
  MOOTORI_PORTAL_SSO_SKIP_ONCE_KEY
} from './utils/portal-auth.util';
import { environment } from './environments/environment';

function applyHandoffSession(at: string, rt?: string | null, dn?: string | null, em?: string | null): void {
  localStorage.setItem('mootori_access_token', at);
  const dnTrim = dn?.trim();
  if (dnTrim) localStorage.setItem('mootori_header_user_label', dnTrim.slice(0, 256));
  const emTrim = em?.trim();
  if (emTrim) localStorage.setItem('mootori_header_user_email', emTrim.slice(0, 256));
  if (rt?.trim()) localStorage.setItem('mootori_refresh_token', rt.trim());
  else localStorage.removeItem('mootori_refresh_token');
}

async function consumeTokenHandoffFromErp(): Promise<void> {
  try {
    const u = new URL(window.location.href);
    const scRaw = u.searchParams.get('sc');
    if (!scRaw?.trim()) return;
    const sc = scRaw.trim();
    const resp = await fetch(`${environment.authUrl}/sso/handoff-exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': String(environment.APIKEY || '').trim()
      },
      body: JSON.stringify({ code: sc })
    });
    if (!resp.ok) return;
    const payload = (await resp.json()) as {
      success?: boolean;
      data?: {
        token?: string;
        tokens?: { accessToken?: string; refreshToken?: string };
        user?: { name?: string; username?: string; email?: string; useremail?: string };
      };
    };
    const access = String(payload?.data?.tokens?.accessToken || payload?.data?.token || '').trim();
    if (!access) return;
    const refresh = String(payload?.data?.tokens?.refreshToken || '').trim();
    const user = payload?.data?.user || {};
    applyHandoffSession(access, refresh, user.name || user.username || null, user.email || user.useremail || null);
    u.searchParams.delete('sc');
    u.searchParams.delete('at');
    u.searchParams.delete('rt');
    u.searchParams.delete('dn');
    u.searchParams.delete('em');
    const qsAfterSc = u.searchParams.toString();
    window.history.replaceState({}, '', u.pathname + (qsAfterSc ? `?${qsAfterSc}` : '') + u.hash);
    sessionStorage.removeItem(MOOTORI_ERP_SSO_ROUNDTRIP_KEY);
    sessionStorage.removeItem('mootori_portal_sso_roundtrip');
  } catch {
    /* ignore */
  }
}

/**
 * Previously: full-page redirect to ERP `portal-bridge` when the portal had no JWT, so an existing
 * ERP session could be copied here. That made every anonymous visit flash erp.mootori.com then
 * bounce back (portal → ERP → portal). Disabled on purpose for the marketing site.
 *
 * Session still arrives via Login/Register handoff (`?sc=` → {@link consumeTokenHandoffFromErp}).
 * Skip-once after logout is still honored so a later re-enable of sync would not fight sign-out.
 */
function clearPortalSsoSkipOnceIfSet(): void {
  try {
    if (sessionStorage.getItem(MOOTORI_PORTAL_SSO_SKIP_ONCE_KEY) === '1') {
      sessionStorage.removeItem(MOOTORI_PORTAL_SSO_SKIP_ONCE_KEY);
    }
  } catch {
    /* ignore */
  }
}

void (async () => {
  await consumeTokenHandoffFromErp();
  clearPortalSsoSkipOnceIfSet();
  platformBrowserDynamic().bootstrapModule(AppModule).catch((err) => console.error(err));
})();
